import {
  DestinationEntityRecordStatus,
  IDestinationEntityHandler,
  IDestinationEntityJobRunOptions,
  IDestinationJobFilter,
  IDestinationJobFilterHandler,
  IGetRecordResponse,
  ISourceEntityHandler,
  ISourceEntityRecord,
  ISourceEntityTypes,
  SkippedDestinationEntityRecord,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
  type MergeConflictDestinationEntityRecord,
  type MissingCustomMappingDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import { IArraySorter } from '@principle-theorem/principle-core/interfaces';
import {
  Postgres,
  PrimaryKey,
  WithPrimaryKey,
  find$,
  getEnumValues,
  multiMap,
  where,
  type IReffable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, isEqual } from 'lodash';
import { from, iif, of, type Observable } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import { PracticeMigration } from '../practice-migrations';
import { buildFilterMigratedQuery } from '../source/source-entity-record';
import { type TranslationMapHandler } from '../translation-map';
import { DestinationEntity } from './destination-entity';

export abstract class BaseDestinationEntity<
  SuccessRecordData extends object,
  InitialJobData extends object,
  BuiltMigrationData extends object,
  MigratedWithErrorsData extends object = object,
> implements
    IDestinationEntityHandler<
      SuccessRecordData,
      InitialJobData,
      BuiltMigrationData
    >
{
  filters: IDestinationJobFilterHandler<InitialJobData>[] = [];
  sorters: IArraySorter[] = [];
  batchLimit = 10000;
  concurrency = 1;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abstract sourceCountComparison: ISourceEntityHandler<any>;
  abstract destinationEntity: IDestinationEntity;
  abstract getDestinationEntityRecordUid(data: InitialJobData): string;

  abstract sourceCountDataAccessor(
    data: InitialJobData
  ): PrimaryKey<ISourceEntityRecord>;

  abstract buildJobData$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<InitialJobData[]>;

  abstract buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: InitialJobData
  ):
    | Promise<
        | BuiltMigrationData
        | (IDestinationEntityRecord & FailedDestinationEntityRecord)
        | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
        | (IDestinationEntityRecord &
            MissingCustomMappingDestinationEntityRecord)
      >
    | (
        | BuiltMigrationData
        | (IDestinationEntityRecord & FailedDestinationEntityRecord)
        | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
        | (IDestinationEntityRecord &
            MissingCustomMappingDestinationEntityRecord)
      );

  abstract hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: BuiltMigrationData
  ): Promise<BuiltMigrationData | undefined> | (BuiltMigrationData | undefined);

  abstract buildMergeConflictRecord(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: InitialJobData,
    migrationData: BuiltMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord;

  abstract runJob(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: InitialJobData,
    migrationData: BuiltMigrationData
  ): Promise<IDestinationEntityRecord>;

  abstract buildSkippedResponse(
    data: InitialJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord;

  canMigrateByDateRange = false;
  canMigrateByIdRange = false;
  sourceEntities = {};
  destinationEntities = {};

  canHandle(destination: IDestinationEntity): boolean {
    return isEqual(
      destination.metadata.key,
      this.destinationEntity.metadata.key
    );
  }

  buildSourceRecordQuery$<Source extends ISourceEntityTypes>(
    migration: WithRef<IPracticeMigration>,
    sourceEntity: ISourceEntityHandler<Source>,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IGetRecordResponse<Source>[]> {
    // eslint-disable-next-line no-console
    console.log(
      `Base Destination Entity: Building job source migration query with batch limit ${this.batchLimit}`
    );
    const sourceRecords$ = sourceEntity.getRecords$(
      migration,
      1000,
      this.batchLimit,
      buildFilterMigratedQuery(runOptions.runType, this.destinationEntity)
    );

    const failedRecords$ = this.getFailedSourceEntities$<Source>(
      migration,
      1000,
      this.batchLimit
    ).pipe(multiMap((source) => sourceEntity.combineRecordWithData(source)));

    return iif(
      () => runOptions.runType === 'retryFailed',
      sourceRecords$.pipe(
        concatMap((records) => (records.length ? of(records) : failedRecords$))
      ),
      sourceRecords$
    );
  }

  /**
   * @deprecated use sourceEntity.getRecords$ with retryFailed as true instead
   */
  getFailedSourceEntities$<Source extends ISourceEntityTypes>(
    migration: WithRef<IPracticeMigration>,
    bufferSize: number = 100,
    limit?: number
  ): Observable<WithPrimaryKey<ISourceEntityRecord<Source>>[]> {
    return from(this.getDestinationEntity(migration)).pipe(
      concatMap((destinationEntity) =>
        Postgres.bufferedQuery$(
          DestinationEntity.resolveSourceRecords,
          {
            migration,
            destinationEntity: destinationEntity,
            statuses: [DestinationEntityRecordStatus.Failed],
            limit: bufferSize,
          },
          undefined,
          limit
        )
      ),
      map(compact)
    );
  }

  getEntity$(
    migration: IReffable<IPracticeMigration>
  ): Observable<WithRef<IDestinationEntity> | undefined> {
    return find$(
      PracticeMigration.destinationEntityCol(migration),
      where('metadata.key', '==', this.destinationEntity.metadata.key)
    );
  }

  async getDestinationEntity(
    migration: IReffable<IPracticeMigration>
  ): Promise<WithRef<IDestinationEntity>> {
    const destinationEntity = await PracticeMigration.findDestinationEntity(
      migration,
      this.destinationEntity
    );
    if (!destinationEntity) {
      throw new Error(
        `Could not find destination entity ${this.destinationEntity.metadata.label}`
      );
    }
    return destinationEntity;
  }

  getRecords$(
    migration: WithRef<IPracticeMigration>,
    bufferSize: number = 50,
    _initialRecord?: WithPrimaryKey<IDestinationEntityRecord>,
    statuses: DestinationEntityRecordStatus[] = getEnumValues(
      DestinationEntityRecordStatus
    )
  ): Observable<WithPrimaryKey<IDestinationEntityRecord<SuccessRecordData>>[]> {
    return from(this.getDestinationEntity(migration)).pipe(
      concatMap((destinationEntity) =>
        Postgres.bufferedQuery$(DestinationEntity.records<SuccessRecordData>, {
          migration,
          destinationEntity,
          limit: bufferSize,
          statuses,
        })
      )
    );
  }

  async getRecord<
    Data extends object = object,
    FailData extends object = object,
    SourceKey extends object = object,
  >(
    migration: WithRef<IPracticeMigration>,
    recordId: string | number
  ): Promise<
    | WithPrimaryKey<IDestinationEntityRecord<Data, FailData, SourceKey>>
    | undefined
  > {
    const destinationEntity = await this.getDestinationEntity(migration);
    return DestinationEntity.getRecord(migration, destinationEntity, recordId);
  }

  shouldSkipRecord(
    jobData: InitialJobData,
    filters: IDestinationJobFilter[]
  ): boolean {
    return filters.some((filter) => {
      const handler = this.filters.find((filterHandler) =>
        filterHandler.canHandle(filter)
      );
      if (!handler) {
        throw new Error(`No filter handler for ${filter.key}`);
      }

      return !handler.matches(jobData, filter);
    });
  }

  getMigrationsWithErrors$(
    migration: WithRef<IPracticeMigration>,
    statuses: DestinationEntityRecordStatus[] = [
      DestinationEntityRecordStatus.MigratedWithErrors,
    ]
  ): Observable<WithPrimaryKey<MigratedWithErrorsData>[]> {
    return from(this.getDestinationEntity(migration)).pipe(
      switchMap(async (destinationEntity) =>
        DestinationEntity.records({
          migration,
          destinationEntity,
          statuses,
        })
      )
    ) as unknown as Observable<WithPrimaryKey<MigratedWithErrorsData>[]>;
  }
}
